import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
import { useState, useMemo, useEffect, useRef } from "react";
import { Check, ChevronsUpDown, Search, X, Loader2 } from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { cn } from "@/lib/utils";

export interface MultipleComboboxItem {
  id: number | string;
  label?: string;
  value?: number | string;
  subtitle?: string;
  raw?: any;
}

export interface MultipleComboboxApiConfig {
  apiFunction: (params: any) => any;
  searchParams: (searchText: string, additionalParams?: any) => any;
  dataPath: string;
  mapToItem: (item: any) => MultipleComboboxItem;
}

export type FormMultipleComboboxAsyncProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  description?: string;
  required?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
  descriptionClassName?: string;
  messageClassName?: string;
  disabled?: boolean;
  apiConfig: MultipleComboboxApiConfig;
  onSelectItems?: (items: MultipleComboboxItem[]) => void;
  debounceDelay?: number;
  minSearchLength?: number;
  maxHeight?: string;
  additionalParams?: any;
  excludeIds?: (number | string)[];
  preserveMessageSpace?: boolean;
  maxDisplayBadges?: number;
  /** Initial items to populate the cache for displaying selected items on first load */
  initialItems?: MultipleComboboxItem[];
};

const DEFAULT_MAX_BADGES = 3;

export const FormMultipleComboboxAsync = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  label,
  name,
  placeholder = "Select items...",
  searchPlaceholder = "Search...",
  description,
  wrapperClassName,
  labelClassName,
  triggerClassName,
  contentClassName,
  descriptionClassName,
  messageClassName,
  disabled = false,
  apiConfig,
  onSelectItems,
  debounceDelay = 800,
  minSearchLength = 0,
  maxHeight = "240px",
  additionalParams,
  excludeIds = [],
  required,
  preserveMessageSpace = false,
  maxDisplayBadges = DEFAULT_MAX_BADGES,
  initialItems = [],
  ...props
}: FormMultipleComboboxAsyncProps<TFieldValues, TName>) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const debouncedSearchText = useDebounce(searchText, debounceDelay);

  // Only fetch when popover is open
  const shouldFetch = open && debouncedSearchText.length >= minSearchLength;

  const { data, isLoading } = useGetData(
    apiConfig.apiFunction(
      apiConfig.searchParams(debouncedSearchText, additionalParams)
    ),
    { enabled: shouldFetch }
  );

  const items = useMemo(() => {
    if (!data) return [];
    const pathSegments = apiConfig.dataPath.split(".");
    let result: any = data;
    for (const segment of pathSegments) {
      result = result?.[segment];
    }
    let mapped = Array.isArray(result) ? result.map(apiConfig.mapToItem) : [];

    if (excludeIds && excludeIds.length > 0) {
      mapped = mapped.filter((item) => !excludeIds.includes(item.id));
    }
    return mapped;
  }, [data, apiConfig, excludeIds]);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchText("");
    }
  };

  const isSearching = searchText !== debouncedSearchText;
  const showLoading = isLoading || isSearching;

  // Track selected items separately to display badges even when not in current search results
  const [selectedItemsCache, setSelectedItemsCache] =
    useState<MultipleComboboxItem[]>(initialItems);

  // Track if initialItems have been applied to avoid re-applying on every render
  const initialItemsAppliedRef = useRef(false);

  // Sync initialItems to cache when they change (e.g., when form loads data)
  useEffect(() => {
    if (initialItems.length > 0 && !initialItemsAppliedRef.current) {
      setSelectedItemsCache((prev) => {
        const map = new Map<number | string, MultipleComboboxItem>();
        prev.forEach((item) => map.set(item.id, item));
        initialItems.forEach((item) => map.set(item.id, item));
        return Array.from(map.values());
      });
      initialItemsAppliedRef.current = true;
    }
  }, [initialItems]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        // field.value is array of ids
        const selectedIds: (number | string)[] = field.value || [];

        // Merge items from cache and current search results
        const allKnownItems = useMemo(() => {
          const map = new Map<number | string, MultipleComboboxItem>();
          selectedItemsCache.forEach((item) => map.set(item.id, item));
          items.forEach((item) => map.set(item.id, item));
          return Array.from(map.values());
        }, [selectedItemsCache, items]);

        // Get selected items for display
        const selectedItems = allKnownItems.filter((item) =>
          selectedIds.includes(item.id)
        );

        const displayedBadges = selectedItems.slice(0, maxDisplayBadges);
        const remainingCount = selectedItems.length - maxDisplayBadges;

        const handleToggleItem = (item: MultipleComboboxItem) => {
          const isSelected = selectedIds.includes(item.id);
          let newIds: (number | string)[];

          if (isSelected) {
            newIds = selectedIds.filter((id) => id !== item.id);
          } else {
            newIds = [...selectedIds, item.id];
            // Add to cache when selecting
            if (!selectedItemsCache.find((cached) => cached.id === item.id)) {
              setSelectedItemsCache((prev) => [...prev, item]);
            }
          }

          field.onChange(newIds);

          if (onSelectItems) {
            const newSelectedItems = allKnownItems.filter((i) =>
              newIds.includes(i.id)
            );
            onSelectItems(newSelectedItems);
          }
        };

        const handleSelectAll = () => {
          const allItemIds = items.map((item) => item.id);
          const allSelected = allItemIds.every((id) =>
            selectedIds.includes(id)
          );

          let newIds: (number | string)[];
          if (allSelected) {
            // Deselect all current items (but keep others that might be selected from previous searches)
            newIds = selectedIds.filter((id) => !allItemIds.includes(id));
          } else {
            // Select all current items
            newIds = [...new Set([...selectedIds, ...allItemIds])];
            // Add all to cache
            setSelectedItemsCache((prev) => {
              const map = new Map<number | string, MultipleComboboxItem>();
              prev.forEach((item) => map.set(item.id, item));
              items.forEach((item) => map.set(item.id, item));
              return Array.from(map.values());
            });
          }

          field.onChange(newIds);

          if (onSelectItems) {
            const newSelectedItems = allKnownItems.filter((i) =>
              newIds.includes(i.id)
            );
            onSelectItems(newSelectedItems);
          }
        };

        const handleClearAll = () => {
          field.onChange([]);
          setSelectedItemsCache([]);
          if (onSelectItems) {
            onSelectItems([]);
          }
        };

        const allCurrentItemsSelected =
          items.length > 0 &&
          items.every((item) => selectedIds.includes(item.id));

        return (
          <FormItem
            className={wrapperClassName}
            aria-invalid={!!fieldState?.error}
          >
            {label && (
              <FormLabel
                className={cn(
                  required &&
                    "aero:flex aero:items-center aero:justify-between",
                  labelClassName
                )}
              >
                {label}
                {required && (
                  <span className="aero:text-destructive" aria-hidden="true">
                    *
                  </span>
                )}
              </FormLabel>
            )}
            <FormControl>
              <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                  <div
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                      "aero:flex aero:w-full aero:items-center aero:justify-between aero:gap-2",
                      "aero:min-h-8 aero:px-4 aero:py-2",
                      "aero:bg-white aero:border aero:border-neutral-300 aero:rounded",
                      "aero:shadow-none",
                      "aero:font-inter aero:font-normal aero:text-sm aero:leading-5",
                      "aero:cursor-pointer",
                      "aero:focus-visible:outline-none aero:focus-visible:border-ring aero:focus-visible:ring-ring/50 aero:focus-visible:ring-[3px]",
                      fieldState?.error &&
                        "aero:border-destructive aero:ring-destructive/20",
                      disabled && "aero:cursor-not-allowed aero:opacity-50",
                      triggerClassName
                    )}
                  >
                    <span
                      className={cn(
                        "aero:truncate aero:font-inter aero:font-normal aero:text-sm aero:leading-5",
                        selectedIds.length > 0
                          ? "aero:text-foreground"
                          : "aero:text-neutral-500"
                      )}
                    >
                      {selectedIds.length > 0 ? (
                        <div className="aero:flex aero:items-center aero:gap-2 aero:flex-wrap ">
                          {displayedBadges.map((item) => (
                            <Badge
                              key={item.id}
                              variant="readonly"
                              className="aero:bg-brand-primary-50 aero:text-brand-primary-600"
                            >
                              {item.label}
                            </Badge>
                          ))}
                          {remainingCount > 0 && (
                            <Badge
                              variant="readonly"
                              className="aero:bg-brand-primary-50 aero:text-brand-primary-600"
                            >
                              +{remainingCount} more
                            </Badge>
                          )}
                        </div>
                      ) : (
                        placeholder
                      )}
                    </span>
                    <ChevronsUpDown className="aero:h-4 aero:w-4 aero:shrink-0 aero:opacity-50" />
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  className={cn(
                    "aero:w-(--radix-popover-trigger-width) aero:p-0",
                    contentClassName
                  )}
                  align="start"
                >
                  {/* Search Input */}
                  <div className="aero:flex aero:items-center aero:border-b aero:px-3">
                    <Search className="aero:h-4 aero:w-4 aero:shrink-0 aero:opacity-50" />
                    <Input
                      placeholder={searchPlaceholder}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="aero:h-9 aero:border-0 aero:shadow-none aero:focus-visible:ring-0"
                    />
                    {searchText && (
                      <span
                        role="button"
                        className="aero:p-1 aero:rounded-full aero:hover:bg-muted aero:cursor-pointer"
                        onClick={() => setSearchText("")}
                      >
                        <X className="aero:h-3 aero:w-3 aero:opacity-50" />
                      </span>
                    )}
                  </div>

                  {/* Selected count & Clear all */}
                  {selectedIds.length > 0 && (
                    <div className="aero:flex aero:items-center aero:justify-between aero:px-3 aero:py-2 aero:border-b aero:bg-muted/50">
                      <span className="aero:text-xs aero:text-muted-foreground">
                        {selectedIds.length} selected
                      </span>
                      <button
                        type="button"
                        onClick={handleClearAll}
                        className="aero:text-xs aero:text-destructive aero:hover:underline"
                      >
                        Clear all
                      </button>
                    </div>
                  )}

                  {/* Content */}
                  <ScrollArea style={{ height: maxHeight }}>
                    {showLoading ? (
                      <div className="aero:flex aero:items-center aero:justify-center aero:py-6">
                        <Loader2 className="aero:h-5 aero:w-5 aero:animate-spin aero:text-muted-foreground" />
                        <span className="aero:ml-2 aero:text-sm aero:text-muted-foreground">
                          Loading...
                        </span>
                      </div>
                    ) : !shouldFetch && minSearchLength > 0 ? (
                      <div className="aero:m-2 aero:flex aero:flex-col aero:items-center aero:justify-center aero:gap-2 aero:rounded aero:border aero:border-neutral-300 aero:bg-neutral-100 aero:py-6 aero:px-2">
                        <span className="aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-center aero:text-muted-foreground">
                          Type at least {minSearchLength} character
                          {minSearchLength > 1 ? "s" : ""} to search...
                        </span>
                      </div>
                    ) : items.length === 0 ? (
                      <div className="aero:m-2 aero:flex aero:flex-col aero:items-center aero:justify-center aero:gap-2 aero:rounded aero:border aero:border-neutral-300 aero:bg-neutral-100 aero:py-6 aero:px-2">
                        <span className="aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-center aero:text-foreground">
                          No results found
                        </span>
                      </div>
                    ) : (
                      <div className="aero:p-1">
                        {/* Select All / Deselect All for current results */}
                        <div
                          role="option"
                          className={cn(
                            "aero:relative aero:flex aero:cursor-pointer aero:select-none aero:items-center aero:rounded-sm aero:px-2 aero:py-1.5 aero:text-sm aero:outline-none aero:font-medium",
                            "aero:hover:bg-accent aero:hover:text-accent-foreground"
                          )}
                          onClick={handleSelectAll}
                        >
                          <span className="aero:truncate">
                            {allCurrentItemsSelected
                              ? "Deselect all"
                              : "Select all"}
                          </span>
                          {allCurrentItemsSelected && (
                            <Check className="aero:ml-auto aero:h-4 aero:w-4" />
                          )}
                        </div>

                        {/* Items */}
                        {items.map((item) => {
                          const isSelected = selectedIds.includes(item.id);

                          return (
                            <div
                              key={item.id}
                              role="option"
                              aria-selected={isSelected}
                              className={cn(
                                "aero:relative aero:flex aero:cursor-pointer aero:select-none aero:items-center aero:rounded-sm aero:px-2 aero:py-1.5 aero:text-sm aero:outline-none",
                                "aero:hover:bg-accent aero:hover:text-accent-foreground",
                                isSelected &&
                                  "aero:bg-accent aero:text-accent-foreground"
                              )}
                              onClick={() => handleToggleItem(item)}
                            >
                              <div className="aero:flex aero:flex-col aero:gap-0.5 aero:flex-1 aero:min-w-0">
                                <span className="aero:truncate">
                                  {item.label}
                                </span>
                                {item.subtitle && (
                                  <span className="aero:text-xs aero:text-muted-foreground aero:truncate">
                                    {item.subtitle}
                                  </span>
                                )}
                              </div>
                              {isSelected && (
                                <Check className="aero:ml-auto aero:h-4 aero:w-4 aero:shrink-0" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </FormControl>
            {description && (
              <FormDescription className={descriptionClassName}>
                {description}
              </FormDescription>
            )}
            <div
              className={cn(preserveMessageSpace ? "aero:min-h-[18px]" : "")}
            >
              <FormMessage
                className={cn(
                  fieldState?.error
                    ? "aero:text-destructive aero:text-xs"
                    : preserveMessageSpace
                      ? "aero:invisible aero:text-xs"
                      : "aero:hidden",
                  messageClassName
                )}
              />
            </div>
          </FormItem>
        );
      }}
      {...props}
    />
  );
};
